import React from 'react';
import { gikClassPrefix } from '@gik/core/constants';
import { useProducts } from '../../api/products';
import { FormApi } from 'final-form';
import { Form } from '@gik/ui/Form';
import { FormProps, Image, Decoder, FrameBone, ParagraphBone } from '@gik/ui';
import ProductPrice from '@gik/ui/ProductPrice';
import { productHasIntegratedCheckout } from '@gik/checkout/utils/productUtils';
import * as validator from '@gik/core/utils/validator';
import i18n from '@gik/i18n';
import { formatCurrency } from '@gik/core/utils/format';
import { CartItem } from '@gik/checkout';
import { Product } from '@gik/core/models/gik/Product';
import { ProductActions, ProductDescription } from '@gik/product-page';
import { ProductSectionContext } from '@gik/product-page/components/ProductSection/ProductSection';
import { useDevStore } from '@gik/core/store/DevStore';
import withSuspense from '@gik/core/components/suspended/withSuspense';
import bemBlock from '@gik/core/utils/bemBlock';

export interface OrderFormProps extends FormProps {
  productIds: number[];
  initialOrders?: CartItem[];
  setIsOrderFormLoading?(value: boolean): void;
}

const blockName = `${gikClassPrefix}-order-form`;

export interface IOrderForm {
  [key: string]: CartItem;
}

function OrderForm({
  onSubmit,
  onChange,
  setIsOrderFormLoading,
  initialOrders = [],
  productIds,
  ...otherProps
}: OrderFormProps): React.ReactElement {
  const { data: products, error: productError } = useProducts(productIds);

  const skeleton = useDevStore(devStore => devStore.skeleton);
  const isLoading = !products || skeleton;
  const isError = isLoading && productError;

  React.useEffect(() => {
    if (setIsOrderFormLoading) setIsOrderFormLoading(isLoading);
  }, [isLoading, setIsOrderFormLoading]);

  //convert initial values to something the form can use internally
  const initialValues: IOrderForm = {};
  if (products) {
    products.forEach((item, index) => {
      initialValues[`item-${index}`] = initialOrders[index] || {
        checkoutType: item.checkoutType,
        name: item.name,
        productId: item.id,
        price: item.price,
        groupedProductIds: item.groupedProductIds,
      };
      //"select" types should always start with a null price (even if the backend returns an initial price)
      if (item.type === 'select') item.price = initialOrders[index]?.price || null;
    });
  }

  if (productError) return <div>Failed to fetch data</div>;

  function handleOnSubmit(values: CartItem[], form: FormApi<object, object>): void | Promise<void> {
    const cart: CartItem[] = products.map((_product: Product, index: number) => {
      return values[`item-${index}`];
    });

    if (onSubmit) return onSubmit(cart, form);
  }

  function handleOnChange(values: CartItem[]): void | Promise<void> {
    if (onChange) onChange(values);
  }

  function validateForm(values: IOrderForm) {
    const errors = {};
    if (!products) {
      console.warn('no products found while validating');
      return errors;
    }

    products.forEach((product, index) => {
      const itemId = 'item-' + index;
      const cartItem = values[itemId];

      // stop if product could not be found
      if (cartItem) {
        if (!product) {
          console.warn(`product "${product.id}" could not be found`);
          return;
        }

        let price = cartItem.price;
        // if type is "select" the price should always start with null issue/GIK-1635-add-gift-card-service-to-calendar

        if (product.type === 'select' && !cartItem.price) price = null;

        if (product.type !== 'fixed' && validator.isEmpty(price)) {
          errors[itemId] = { message: i18n.t('validation.required') };
        }

        if (product.type === 'select' && validator.isEmpty(price)) {
          errors[itemId] = { message: i18n.t('validation.requiredSelect') };
        }

        if (product.type === 'input') {
          const minValue = product.variations[0]?.minValue;
          const maxValue = product.variations[0]?.maxValue;
          const numValue = price;
          if (validator.isEmpty(numValue)) {
            errors[itemId] = {
              message: i18n.t('validation.requiredInput'),
            };
          }
          if (validator.minValueExclusive(minValue, numValue)) {
            errors[itemId] = {
              message: i18n.t('validation.minValue', {
                count: formatCurrency(minValue, { decimals: false }),
              } as object),
            };
          } else if (validator.maxValueExclusive(maxValue, numValue)) {
            errors[itemId] = {
              message: i18n.t('validation.maxValue', {
                count: formatCurrency(maxValue, { decimals: false }),
              } as object),
            };
          }
        }
      }
    });
    return errors;
  }

  if (isLoading) return <Skeleton />;
  if (isError) return <Skeleton />;

  return (
    <Form
      onSubmit={handleOnSubmit}
      onChange={handleOnChange}
      vertical
      validate={validateForm}
      initialValues={initialValues}
      {...otherProps}
      render={() => {
        return (
          <>
            {products.map((product, index) => {
              const hasIntegratedCheckout = productHasIntegratedCheckout(product);

              let image = null;
              if (product.images?.length) image = product.images[0];

              return (
                <div key={`${product.id}-${index}`} className={`${blockName}__item`}>
                  <header>
                    <figure>
                      <Image src={image} />
                    </figure>
                    <main>
                      <span className={`${blockName}__title`}>
                        <Decoder text={product.name} />
                      </span>
                      {hasIntegratedCheckout && <span className={`${blockName}__subtitle`}>on Give InKind</span>}
                      {!hasIntegratedCheckout && (
                        <span className={`${blockName}__subtitle`}>
                          On{' '}
                          <Decoder text={product.sources && product.sources[0] ? product.sources[0].name : 'Unknown'} />
                        </span>
                      )}
                      <ProductPrice product={product} index={index} />
                    </main>
                  </header>
                  <main>
                    {/*TODO: remove ProductSectionContext as it makes product actions difficult to use outside of ProductSection*/}
                    <ProductSectionContext.Provider
                      value={{
                        product,
                        inkindPageViewHistory: [],
                      }}
                    >
                      <ProductActions showBuyButton={false} />
                      <ProductDescription />
                    </ProductSectionContext.Provider>
                  </main>
                </div>
              );
            })}
          </>
        );
      }}
    />
  );
}

const skelBem = bemBlock('order-form-skeleton');
const Skeleton = () => (
  <div className={skelBem()}>
    <header>
      <main>
        <FrameBone className={skelBem('image')} />
      </main>
      <aside>
        <ParagraphBone words={4} className={skelBem('title')} />
        <ParagraphBone words={3} className={skelBem('on-platform')} />
        <FrameBone className={skelBem('dropdown')} />
      </aside>
    </header>
    <section className={skelBem('user-actions')}>
      <FrameBone className={skelBem('action')} />
      <FrameBone className={skelBem('action')} />
    </section>
    <section>
      <ParagraphBone words={12} className={skelBem('title')} />
      <ParagraphBone words={100} className={skelBem('title')} />
      <ParagraphBone words={100} className={skelBem('title')} />
    </section>
  </div>
);

export default withSuspense(OrderForm, Skeleton);
